import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';

const ENTITY_TYPES = [
  'aircraft',
  'assembly',
  'component',
  'charter',
  'sector',
  'maintenance',
  'amp',
  'adsb',
  'techlog'
];

const SEVERITIES = ['info', 'warning', 'critical'];

const emptyForm = {
  id: null,
  code: '',
  label: '',
  entityTypes: ['component'],
  severity: 'info',
  enabled: true,
  expression: '',
  messageTemplate: '',
  isSystem: false
};

const AnalyticalRules = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const canManage = user?.role === ROLES.SUPER_ADMIN || user?.role === ROLES.MIS;

  const fetchRules = async () => {
    if (!canManage) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/analytics-rules', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(res.data || []);
    } catch (err) {
      console.error('Failed to load analytics rules', err);
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!canManage) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Analytical Rules
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Only SUPER_ADMIN can manage analytical rules.
        </Typography>
      </Box>
    );
  }

  const openNewDialog = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (rule) => {
    setForm({
      id: rule._id,
      code: rule.code,
      label: rule.label,
      entityTypes: rule.entityTypes || [],
      severity: rule.severity || 'info',
      enabled: rule.enabled !== false,
      expression: rule.expression,
      messageTemplate: rule.messageTemplate,
      isSystem: !!rule.isSystem
    });
    setDialogOpen(true);
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.label}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/analytics-rules/${rule._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRules();
    } catch (err) {
      console.error('Failed to delete analytics rule', err);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        code: form.code,
        label: form.label,
        entityTypes: form.entityTypes,
        severity: form.severity,
        enabled: form.enabled,
        expression: form.expression,
        messageTemplate: form.messageTemplate
      };
      if (!form.id) {
        await axios.post('/api/analytics-rules', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(`/api/analytics-rules/${form.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setDialogOpen(false);
      fetchRules();
    } catch (err) {
      console.error('Failed to save analytics rule', err);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Analytical Rules
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage custom data-quality rules for Analytical Tree. Expressions are evaluated against each node&apos;s summary.
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openNewDialog}
        >
          Add Rule
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Entities</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Expression</TableCell>
                <TableCell>Message</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!rules.length && !loading && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">
                      No custom rules defined yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rules.map((rule) => (
                <TableRow key={rule._id} hover>
                  <TableCell>{rule.code}</TableCell>
                  <TableCell>{rule.label}</TableCell>
                  <TableCell>
                    {rule.entityTypes?.map((t) => (
                      <Chip
                        key={t}
                        label={t}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.25 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rule.severity}
                      size="small"
                      color={
                        rule.severity === 'critical'
                          ? 'error'
                          : rule.severity === 'warning'
                          ? 'warning'
                          : 'info'
                      }
                    />
                  </TableCell>
                  <TableCell>{rule.enabled ? 'Yes' : 'No'}</TableCell>
                  <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {rule.expression}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {rule.messageTemplate}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEditDialog(rule)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    {!rule.isSystem && (
                      <IconButton size="small" onClick={() => handleDelete(rule)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.id ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => handleChange('code', e.target.value)}
              size="small"
              disabled={!!form.id}
              helperText="Unique identifier (e.g., COMP_USED_ITEM)."
            />
            <TextField
              label="Label"
              value={form.label}
              onChange={(e) => handleChange('label', e.target.value)}
              size="small"
              disabled={form.isSystem}
            />
            <FormControl size="small" disabled={form.isSystem}>
              <InputLabel>Entity Types</InputLabel>
              <Select
                multiple
                value={form.entityTypes}
                onChange={(e) => handleChange('entityTypes', e.target.value)}
                renderValue={(selected) => selected.join(', ')}
                label="Entity Types"
              >
                {ENTITY_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    <Checkbox checked={form.entityTypes.indexOf(t) > -1} />
                    <ListItemText primary={t} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={form.severity}
                onChange={(e) => handleChange('severity', e.target.value)}
                label="Severity"
              >
                {SEVERITIES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                />
              }
              label="Enabled"
            />
            <TextField
              label="Expression"
              value={form.expression}
              onChange={(e) => handleChange('expression', e.target.value)}
              size="small"
              multiline
              minRows={2}
              helperText="JavaScript expression evaluated with summary, type, id, asOfDate, context. Example: summary.isUsedAtInstallation === true"
              disabled={form.isSystem}
            />
            <TextField
              label="Message"
              value={form.messageTemplate}
              onChange={(e) => handleChange('messageTemplate', e.target.value)}
              size="small"
              multiline
              minRows={2}
              disabled={form.isSystem}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnalyticalRules;

